import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuthContext } from '../auth'
import {
  ProfileService,
  type UserFeatureAccess,
  type GlobalToggleName,
} from '../../services/profile/profileService'
import { useCachedLoader } from '../../hooks/useCachedLoader'
import {
  FeatureAccessContext,
  type FeatureAccessContextType,
  type FeatureName,
  type FeatureCheckResult,
} from './FeatureAccessContextDef'

export function FeatureAccessProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext()
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false)
  const [blockedFeature, setBlockedFeature] = useState<FeatureName | null>(null)

  const {
    data: featureAccess,
    loading,
    reload: refetch,
  } = useCachedLoader<UserFeatureAccess | null>(
    user?.id ? `features-${user.id}` : null,
    async () => ProfileService.getFeatures(),
    { initialValue: null, skipPaths: ['/login'] }
  )

  const checkFeature = useCallback(
    (feature: FeatureName): FeatureCheckResult => {
      const prompt = () => {
        setBlockedFeature(feature)
        setUpgradeModalVisible(true)
      }

      if (!featureAccess) {
        return { hasAccess: true, tier: 'free', showUpgradePrompt: prompt }
      }

      return {
        hasAccess: featureAccess.features[feature] === true,
        tier: featureAccess.tier,
        showUpgradePrompt: prompt,
      }
    },
    [featureAccess]
  )

  const isFeatureEnabled = useCallback(
    (toggle: GlobalToggleName): boolean => {
      if (!featureAccess?.globalToggles) return false
      return featureAccess.globalToggles[toggle] ?? false
    },
    [featureAccess]
  )

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModalVisible(false)
    setBlockedFeature(null)
  }, [])

  const showUpgradeModal = useCallback((feature: FeatureName) => {
    setBlockedFeature(feature)
    setUpgradeModalVisible(true)
  }, [])

  const value = useMemo<FeatureAccessContextType>(
    () => ({
      featureAccess,
      loading,
      refetch,
      checkFeature,
      isFeatureEnabled,
      upgradeModalVisible,
      blockedFeature,
      closeUpgradeModal,
      showUpgradeModal,
      onboardingCompleted: featureAccess?.onboardingCompleted ?? false,
    }),
    [
      featureAccess,
      loading,
      refetch,
      checkFeature,
      isFeatureEnabled,
      upgradeModalVisible,
      blockedFeature,
      closeUpgradeModal,
      showUpgradeModal,
    ]
  )

  return <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>
}
